import Avatar from 'components/base/Avatar';
import { useEffect, useRef, useState } from 'react';
import { Card, Dropdown, Nav } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { useNavigate } from 'react-router-dom';
import Scrollbar from 'components/base/Scrollbar';
import classNames from 'classnames';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/rootReducer';
import { clearCookie, getCookie, setCookie } from 'utils/cookieUtils';
import {
  ACCESS_TOKEN,
  ENGLISH,
  GERMAN,
  SELECTED_LANGUAGE
} from 'utils/enums/misc';
import { FAQ, LOGIN } from 'constant/RoutesUrls';
import './ProfileDropdownMenu.scss';
import { ChangeNameModal } from 'components/base/ChangeNameModal';
import StandardToggle from '../../../base/StandardToggle/StandardToggle';
import { UilQuestionCircle } from '@iconscout/react-unicons';
import * as userProfileAction from '../../../redux/userProfile/userProfileAction';
import { useAppDispatch } from '../../../redux/HooksRedux';

const DEBOUNCE_MS = 600;

const ProfileDropdownMenu = ({
  className,
  onClose
}: {
  className?: string;
  onClose?: () => void;
}) => {
  const userProfile = useSelector(
    (state: RootState) => state.userProfile.userProfile
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [selectedValue, setSelectedValue] = useState(GERMAN);
  const [showChangeNamePopup, setShowChangeNamePopup] = useState(false);
  const [localEmailConfig, setLocalEmailConfig] = useState(
    userProfile.email_configurations
  );
  const [isEmailConfigLoading, setIsEmailConfigLoading] = useState(false);
  const [emailConfigError, setEmailConfigError] = useState<string | null>(null);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isEmailConfigLoading) {
      setLocalEmailConfig(userProfile.email_configurations);
    }
  }, [userProfile.email_configurations]);

  useEffect(() => {
    const language = getCookie(SELECTED_LANGUAGE);
    if (language) {
      setSelectedValue(language);
    } else {
      setCookie(SELECTED_LANGUAGE, GERMAN);
    }
  }, []);

  const handleLogout = () => {
    clearCookie(ACCESS_TOKEN);
    navigate(LOGIN);
  };

  const changeLanguage = (lang: typeof ENGLISH | typeof GERMAN) => {
    setCookie(SELECTED_LANGUAGE, lang);
    window.location.reload();
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isEmailConfigLoading) return;

    const previousValue = localEmailConfig;
    const newValue = !localEmailConfig;

    // Lock & optimistically update immediately
    setLocalEmailConfig(newValue);
    setIsEmailConfigLoading(true);
    setEmailConfigError(null);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        await dispatch(
          userProfileAction.updateEmailConfig(
            newValue,
            userProfile.user_id?.toString(),
            userProfile.first_name,
            userProfile.last_name
          )
        );
        await dispatch(userProfileAction.getProfile());
      } catch {
        setLocalEmailConfig(previousValue);
        setEmailConfigError(t('header.drop_down.email_config_error'));
      } finally {
        setIsEmailConfigLoading(false);
      }
    }, DEBOUNCE_MS);
  };

  return (
    <Dropdown.Menu
      align="end"
      className={classNames(
        className,
        'navbar-top-dropdown-menu navbar-dropdown-caret py-0 dropdown-profile shadow border'
      )}
    >
      <Card className="position-relative border-0">
        <Card.Body className="p-0">
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 pt-4 pb-3">
            <Avatar variant="name" size="xl">
              <span>
                {userProfile.first_name.charAt(0) +
                  userProfile.last_name.charAt(0)}
              </span>
            </Avatar>
            <h6 className="text-body-emphasis">
              {userProfile.first_name + ' ' + userProfile.last_name}
            </h6>
          </div>

          <div className="profile-card-height">
            <Scrollbar>
              <Nav className="nav flex-column pb-1 scrollbar">
                <Nav.Item>
                  <Nav.Link className="px-3">
                    <Dropdown className="w-100">
                      <Dropdown.Toggle className="bg-transparent text-body border-0 p-0 d-flex justify-content-start align-items-center w-100 custom-dropdown-toggle">
                        <FeatherIcon
                          icon="globe"
                          size={16}
                          className="me-2 text-body"
                        />
                        <span className="text-body-highlight">
                          {t('login_signup.language')}
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="p-0">
                        <Dropdown.Item
                          onClick={() => changeLanguage(ENGLISH)}
                          className={
                            selectedValue === ENGLISH
                              ? 'bg-danger text-white'
                              : ''
                          }
                        >
                          {t('header.drop_down.value_3')}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => changeLanguage(GERMAN)}
                          className={
                            selectedValue === GERMAN
                              ? 'bg-danger text-white'
                              : ''
                          }
                        >
                          {t('header.drop_down.value_4')}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item onClick={() => setShowChangeNamePopup(true)}>
                  <Nav.Link className="px-3">
                    <FeatherIcon
                      icon="user"
                      size={16}
                      className="me-2 text-body"
                    />
                    <span className="text-body-highlight">
                      {userProfile.first_name} {userProfile.last_name}
                    </span>
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link className="px-3">
                    <FeatherIcon
                      icon="mail"
                      size={16}
                      className="me-2 text-body"
                    />
                    <span className="text-body-highlight">
                      {userProfile.email}
                    </span>
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item
                  onClick={() => {
                    onClose?.();
                    window.open(FAQ, '_blank');
                  }}
                >
                  <Nav.Link className="px-3">
                    <UilQuestionCircle size={16} className="me-2 text-body" />
                    <span className="text-body-highlight">
                      {t('header.text.text_15')}
                    </span>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Scrollbar>
          </div>

          <div className="email-config-section border-top border-translucent">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <FeatherIcon
                  icon="bell"
                  size={16}
                  className="text-body flex-shrink-0"
                />
                <span className="text-body-highlight fw-semibold">
                  {t('header.drop_down.email_config_label')}
                </span>
              </div>
              <StandardToggle
                checked={!!localEmailConfig}
                onChange={handleToggleClick}
                loading={isEmailConfigLoading}
              />
            </div>
            <p className="mb-0 mt-1 text-body-tertiary email-config-description">
              {t('header.drop_down.email_config_description')}
            </p>
            {emailConfigError && (
              <p className="mb-0 mt-1 text-danger email-config-description">
                {emailConfigError}
              </p>
            )}
          </div>
        </Card.Body>

        <Card.Footer className="p-0 border-top border-translucent">
          <div className="my-2" />
          <div className="px-3" onClick={handleLogout}>
            <div className="btn btn-phoenix-secondary d-flex flex-center w-100">
              <FeatherIcon icon="log-out" className="me-2" size={16} />
              {t('header.drop_down.value_2')}
            </div>
          </div>
          <div className="my-2" />
        </Card.Footer>
      </Card>

      {showChangeNamePopup && (
        <ChangeNameModal
          onCancel={() => setShowChangeNamePopup(false)}
          onConfirm={() => {}}
          show={showChangeNamePopup}
        />
      )}
    </Dropdown.Menu>
  );
};

export default ProfileDropdownMenu;
